import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { safeReturnPath } from '../services/authNavigation';

const ModernLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { login, isAuthenticated, authError, retryAuth, loading: checkingAccess } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = safeReturnPath(
    new URLSearchParams(location.search).get('returnTo') || location.state?.from
  );

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
    }
  }, [location]);

  useEffect(() => {
    if (isAuthenticated && !authError && !checkingAccess) {
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, authError, checkingAccess, navigate, returnTo]);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    try {
      setError('');
      setLoading(true);
      const success = await login(username, password);

      if (!success) {
        setError(
          'We could not finish checking your access. Retry the access check below, or sign in again.'
        );
      }
    } catch (err) {
      setError(
        [400, 401].includes(err.response?.status)
          ? 'Invalid username or password.'
          : 'The sign-in service is unavailable. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-page">
      <div className="w-full">
        <div>
          {/* Header */}
          <div className="account-header">
            <h1 className="account-title">Sign in to OikoBrowser</h1>
            <p className="account-description">Continue to your datasets and research tools.</p>
          </div>

          {/* Form Card */}
          <div className="max-w-md mx-auto">
            <div className="account-form">
              {successMessage && (
                <div className="alert-success mb-6" role="status">
                  <p className="text-sm font-medium">{successMessage}</p>
                </div>
              )}

              {authError && (
                <div className="alert-error mb-6" role="alert">
                  <p>{authError}</p>
                  <button
                    type="button"
                    className="btn-secondary btn-md mt-3"
                    onClick={retryAuth}
                    disabled={checkingAccess}
                  >
                    {checkingAccess ? 'Checking access…' : 'Retry access check'}
                  </button>
                </div>
              )}
              {error && !authError && (
                <div id="login-error" className="alert-error mb-6" role="alert">
                  <p className="text-sm font-medium">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" aria-busy={loading}>
                <div>
                  <label
                    htmlFor="username"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Username
                  </label>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    aria-describedby={error ? 'login-error' : undefined}
                    required
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    className="input"
                    placeholder="Enter your username"
                  />
                </div>

                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    aria-describedby={error ? 'login-error' : undefined}
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className="input"
                    placeholder="Enter your password"
                  />
                </div>

                <button type="submit" disabled={loading} className="btn-primary btn-lg w-full">
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <span className="spinner mr-2" aria-hidden="true"></span>
                      Signing in...
                    </span>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </form>
              <Link to="/forgot-password" className="workspace-link mt-3">
                Forgot password?
              </Link>
            </div>

            {/* Sign up link */}
            <div className="text-center mt-6">
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <Link to="/register" className="workspace-link">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernLogin;
